import React, { useState, useRef, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';
import { Visibility, VisibilityOff, CheckCircleOutline, CancelOutlined } from '@material-ui/icons';
import { getPasswordChecks } from '../../pages/DQPro/DQProV2/components/PasswordWithValidationsField.comp';

const CHECKLIST_ITEMS = [
    { key: 'minLength', label: 'Minimum 8 characters' },
    { key: 'uppercase', label: 'At least one uppercase letter' },
    { key: 'lowercase', label: 'At least one lowercase letter' },
    { key: 'digit', label: 'At least one numeric digit' },
    { key: 'special', label: 'At least one special character' },
];

/**
 * MUI TextField wrapped with the same show/hide-password toggle and live
 * strength checklist popover used on the Register/Signup page
 * (PasswordWithValidationsField.comp.jsx). Callers pass their own MUI
 * TextField/InputAdornment/IconButton/Box/Typography via `components` so
 * this works whether the page uses MUI v4 (@material-ui/core) or v5
 * (@mui/material) — those two aren't API-identical, so a fixed import
 * here would break one or the other.
 *
 * The checklist renders via a portal into document.body, positioned from
 * the field's live bounding rect — MUI's Card (and other containers) clip
 * overflowing content by default, which silently hid an absolutely
 * positioned popover nested inside one. A portal escapes that regardless
 * of which container a caller wraps this field in.
 */
const PasswordFieldWithChecklist = ({
    components: { TextField, InputAdornment, IconButton, Box, Typography },
    showChecklist = false,
    value,
    onFocus,
    onBlur,
    ...textFieldProps
}) => {
    const [showPassword, setShowPassword] = useState(false);
    const [focused, setFocused] = useState(false);
    const [popoverPos, setPopoverPos] = useState(null);
    const wrapperRef = useRef(null);
    const checks = showChecklist ? getPasswordChecks(value) : null;

    useLayoutEffect(() => {
        if (showChecklist && focused && wrapperRef.current) {
            const rect = wrapperRef.current.getBoundingClientRect();
            setPopoverPos({ top: rect.top - 8, left: rect.left, width: rect.width });
        } else {
            setPopoverPos(null);
        }
    }, [showChecklist, focused]);

    return (
        <div ref={wrapperRef} style={{ position: 'relative', width: '100%' }}>
            {showChecklist && focused && popoverPos && ReactDOM.createPortal(
                <Box
                    sx={{
                        position: 'fixed',
                        top: popoverPos.top,
                        left: popoverPos.left,
                        transform: 'translateY(-100%)',
                        zIndex: 1400,
                        minWidth: Math.max(popoverPos.width, 280),
                        background: '#fff',
                        border: '1px solid #d9dee7',
                        borderRadius: '8px',
                        boxShadow: '0 6px 16px rgba(20, 29, 52, 0.18)',
                        p: '10px 12px',
                    }}
                >
                    <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#1f2937', mb: 0.9 }}>
                        Password must contain:
                    </Typography>
                    {CHECKLIST_ITEMS.map((item) => (
                        <Box key={item.key} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.6 }}>
                            {checks[item.key]
                                ? <CheckCircleOutline style={{ color: '#2e7d32', fontSize: 18 }} />
                                : <CancelOutlined style={{ color: '#d32f2f', fontSize: 18 }} />}
                            <Typography sx={{ fontSize: '12px', color: '#6b7280', lineHeight: 1.2 }}>
                                {item.label}
                            </Typography>
                        </Box>
                    ))}
                </Box>,
                document.body,
            )}
            <TextField
                {...textFieldProps}
                type={showPassword ? 'text' : 'password'}
                value={value}
                onFocus={(e) => {
                    setFocused(true);
                    if (onFocus) onFocus(e);
                }}
                onBlur={(e) => {
                    setFocused(false);
                    if (onBlur) onBlur(e);
                }}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton
                                aria-label="toggle password visibility"
                                onClick={() => setShowPassword((p) => !p)}
                                onMouseDown={(e) => e.preventDefault()}
                                edge="end"
                                tabIndex={-1}
                            >
                                {showPassword
                                    ? <VisibilityOff style={{ fontSize: 20 }} />
                                    : <Visibility style={{ fontSize: 20 }} />}
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
            />
        </div>
    );
};

export default PasswordFieldWithChecklist;
